import { useEffect, useRef } from 'react';
import mapboxgl from 'maplibre-gl';
import cx from 'classnames';
import throttle from 'lodash/throttle';

import settings from '../settings/index.js';

import "../styles/Map.css";
import 'maplibre-gl/dist/maplibre-gl.css';


const CLUSTERS_LAYERS = ['clusters-small', 'clusters-medium', 'clusters-big', 'clusters-enormous'];


export function MapMapbox({
    zoom, maxZoom = 16, minZoom = 1, center, clusters = [],
    marker, showCrosshair = false, disableZoom = false,
    disableDoubleClickZoom = false, onClick = () => null,
    onClusterClick = () => null, onBoundChange = () => null,
    onMapReady = () => null, clustersOpacity = 1, children,
    navigationControlOptions, navigationControlPosition = 'bottom-right',
    className,
}) {
    const containerRef = useRef(null);
    const instanceRef = useRef(null);
    const markerElRef = useRef(null);
    const markerRef = useRef(null);
    const mapLoadedRef = useRef(false);
    const clustersRef = useRef(clusters);
    clustersRef.current = clusters;

    // Stable refs for callbacks used inside map event listeners
    const onBoundChangeRef = useRef(onBoundChange);
    const onMapReadyRef = useRef(onMapReady);
    const onClickRef = useRef(onClick);
    const onClusterClickRef = useRef(onClusterClick);
    onBoundChangeRef.current = onBoundChange;
    onMapReadyRef.current = onMapReady;
    onClickRef.current = onClick;
    onClusterClickRef.current = onClusterClick;

    // Initialize map once
    useEffect(() => {
        const instance = new mapboxgl.Map({
            container: containerRef.current,
            style: settings.MAPBOX_STYLE,
            center,
            zoom,
            maxZoom,
            minZoom
        });
        instanceRef.current = instance;

        instance.dragRotate.disable();
        instance.touchZoomRotate.disableRotation();

        markerElRef.current = document.createElement('div');

        if (disableZoom) instance.scrollZoom.disable();
        if (disableDoubleClickZoom) instance.doubleClickZoom.disable();

        if (navigationControlOptions) {
            instance.addControl(
                new mapboxgl.NavigationControl(navigationControlOptions),
                navigationControlPosition
            );
        }

        instance.on('zoom', throttle(() => onBoundChangeRef.current(instance), 250));
        instance.on('drag', throttle(() => onBoundChangeRef.current(instance), 250));

        instance.once('load', () => {
            mapLoadedRef.current = true;
            onMapReadyRef.current(instance);

            const features = clustersRef.current.map(cluster => {
                const { lon, lat } = cluster.centroid.location;
                return {
                    type: "Feature",
                    properties: { size: cluster.doc_count },
                    geometry: { type: "Point", coordinates: [lon, lat] }
                };
            });

            instance.addSource('clusters', {
                type: 'geojson',
                data: { type: "FeatureCollection", features }
            });

            instance.addLayer({
                id: "clusters-small", type: "circle", source: "clusters",
                filter: ["<", "size", 30],
                paint: { "circle-color": "#26c1c6", "circle-radius": 15, "circle-opacity": clustersOpacity }
            });
            instance.addLayer({
                id: "clusters-small-text", type: "symbol", source: "clusters",
                filter: ["<", "size", 100],
                layout: { "text-field": "{size}", "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"], "text-size": 11, "text-allow-overlap": true },
                paint: { "text-color": "#ffffff" }
            });
            instance.addLayer({
                id: "clusters-medium", type: "circle", source: "clusters",
                filter: ["all", [">=", "size", 30], ["<", "size", 100]],
                paint: { "circle-color": "#ffa92a", "circle-radius": 20, "circle-opacity": clustersOpacity }
            });
            instance.addLayer({
                id: "clusters-medium-text", type: "symbol", source: "clusters",
                filter: ["all", [">=", "size", 30], ["<", "size", 100]],
                layout: { "text-field": "{size}", "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"], "text-size": 11 },
                paint: { "text-color": "#ffffff" }
            });
            instance.addLayer({
                id: "clusters-big", type: "circle", source: "clusters",
                filter: ["all", [">=", "size", 100], ["<", "size", 500]],
                paint: {
                    "circle-radius": 25,
                    "circle-color": ["case", ["boolean", ["feature-state", "hover"], false], "#d41bc0", "#a71597"],
                    "circle-opacity": clustersOpacity
                }
            });
            instance.addLayer({
                id: "clusters-big-text", type: "symbol", source: "clusters",
                filter: ["all", [">=", "size", 100], ["<", "size", 500]],
                layout: { "text-field": "{size}", "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"], "text-size": 11 },
                paint: { "text-color": "#ffffff" }
            });
            instance.addLayer({
                id: "clusters-enormous", type: "circle", source: "clusters",
                filter: [">=", "size", 500],
                paint: { "circle-radius": 35, "circle-color": "#E01A4F", "circle-opacity": clustersOpacity }
            });
            instance.addLayer({
                id: "clusters-enormous-text", type: "symbol", source: "clusters",
                filter: [">=", "size", 500],
                layout: { "text-field": "{size}", "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"], "text-size": 11 },
                paint: { "text-color": "#ffffff" }
            });

            instance.on('click', e => onClickRef.current(instance, e));

            for (const layer of CLUSTERS_LAYERS) {
                instance.on('click', layer, e => onClusterClickRef.current(instance, e));
                instance.on('mouseenter', layer, () => { instance.getCanvas().style.cursor = 'pointer'; });
                instance.on('mouseleave', layer, () => { instance.getCanvas().style.cursor = ''; });
            }
        });

        return () => instance.remove();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Update center/zoom (skip on initial mount, map handles initial values)
    useEffect(() => {
        const instance = instanceRef.current;
        if (!instance || !mapLoadedRef.current) return;
        instance.setCenter(center);
        instance.setZoom(zoom);
    }, [center, zoom]);

    // Update marker
    useEffect(() => {
        const instance = instanceRef.current;
        if (!instance) return;
        if (!marker) {
            markerRef.current?.remove();
            markerRef.current = null;
        } else if (markerRef.current) {
            markerRef.current.setLngLat(marker);
        } else {
            markerRef.current = new mapboxgl.Marker(markerElRef.current)
                .setLngLat(marker)
                .addTo(instance);
        }
    }, [marker]);

    // Update clusters opacity
    useEffect(() => {
        const instance = instanceRef.current;
        if (!instance) return;
        for (const layer of CLUSTERS_LAYERS) {
            if (instance.getLayer(layer)) {
                instance.setPaintProperty(layer, 'circle-opacity', clustersOpacity);
                instance.setPaintProperty(`${layer}-text`, 'text-opacity', clustersOpacity);
            }
        }
    }, [clustersOpacity]);

    // Update clusters data
    useEffect(() => {
        const instance = instanceRef.current;
        if (!instance || !mapLoadedRef.current) return;
        const features = clusters.map(cluster => {
            const { lon, lat } = cluster.centroid.location;
            return {
                type: "Feature",
                properties: { size: cluster.doc_count },
                geometry: { type: "Point", coordinates: [lon, lat] }
            };
        });
        const data = { type: "FeatureCollection", features };
        if (instance.getSource('clusters')) {
            instance.getSource('clusters').setData(data);
        }
    }, [clusters]);

    return (
        <div ref={containerRef} className={cx("Map", className)}>
            {showCrosshair && <div className="target" />}
            {children}
        </div>
    );
}
